import {
  PAPER_PROPERTIES,
  BINDING_SPINE_OFFSET,
  BOOK_COVER_DIMENSIONS,
  BOOK_SIZE,
  type PaperType,
  type BindingType,
} from '../../constants/bookFormFactor';

export type ElementType = 'text' | 'image' | 'rect' | 'circle' | 'line' | 'triangle';

interface BaseElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  visible: boolean;
  locked?: boolean;
  name: string;
}

export interface TextElement extends BaseElement {
  type: 'text';
  text: string;
  fontSize: number;
  fontFamily: string;
  fontStyle: 'normal' | 'bold' | 'italic' | 'bold italic';
  fill: string;
  align: 'left' | 'center' | 'right';
  letterSpacing: number;
  lineHeight: number;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  strokeColor: string;
  strokeWidth: number;
  strokePosition: 'center' | 'outer';
  shadowEnabled: boolean;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
}

export interface ImageElement extends BaseElement {
  type: 'image';
  src: string;
  flipX: boolean;
  flipY: boolean;
  // Tonal adjustments
  brightness?: number;   // -1 to 1
  contrast?: number;     // -100 to 100
  saturation?: number;   // -2 to 2
  temperature?: number;  // -100 (cool) to 100 (warm)
  // Effects
  blur?: number;         // 0 to 20
  grayscale?: boolean;
  sepia?: boolean;
}

export interface RectElement extends BaseElement {
  type: 'rect';
  fill: string;
  stroke: string;
  strokeWidth: number;
  cornerRadius: number;
}

export interface CircleElement extends BaseElement {
  type: 'circle';
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export interface LineElement extends BaseElement {
  type: 'line';
  stroke: string;
  strokeWidth: number;
  cap: 'butt' | 'round' | 'square';
}

export interface TriangleElement extends BaseElement {
  type: 'triangle';
  fill: string;
  stroke: string;
  strokeWidth: number;
}

export type CanvasElement = TextElement | ImageElement | RectElement | CircleElement | LineElement | TriangleElement;

/** Pixel positions of the guide overlay lines — derived from real print dimensions */
export interface CanvasGuides {
  /** Width/height of the bleed band in pixels */
  bleedPx: number;
  /** Back panel width in pixels */
  backWidthPx: number;
  /** Spine width in pixels */
  spineWidthPx: number;
  /** X pixel where the front panel starts */
  frontStartPx: number;
}

export interface CanvasState {
  elements: CanvasElement[];
  selectedId: string | null;
  background: string;
  stageWidth: number;
  stageHeight: number;
  /** Guide overlay positions computed from real print dimensions */
  guides: CanvasGuides;
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const CANVAS_DISPLAY_HEIGHT = 580; // px — fixed screen height

export const BLEED_MARGIN = 0.25; // inches — matches generateCoverTemplate.ts

export interface FontEntry {
  family: string;
  label?: string;
}

export interface FontCategory {
  id: string;
  label: string;
  fonts: FontEntry[];
}

export const COVER_FONT_CATEGORIES: FontCategory[] = [
  {
    id: 'literary',
    label: 'Drama & Literary',
    fonts: [
      { family: 'Fraunces' },       // Variable optical serif — stunning italics, very distinctive
      { family: 'Gloock' },         // Modern editorial serif, beautiful weight contrast
      { family: 'Yeseva One' },     // Display serif with personality
      { family: 'Spectral' },       // Sharp literary — designed for long-form reading
      { family: 'Faustina' },       // Strong editorial slab
      { family: 'DM Serif Display' },
    ],
  },
  {
    id: 'artdeco',
    label: 'Art Deco',
    fonts: [
      { family: 'Poiret One' },     // Art deco perfection — ultra-distinctive geometric
      { family: 'Julius Sans One' },// Ultra-thin elegant art deco
      { family: 'Josefin Slab' },   // Geometric slab, 1920s editorial
      { family: 'Cinzel' },         // Roman capitals, monumental
    ],
  },
  {
    id: 'thriller',
    label: 'Thriller & Dark',
    fonts: [
      { family: 'Bebas Neue' },     // Condensed all-caps — ubiquitous in thrillers for good reason
      { family: 'Anton' },          // Heavy impact, punchy
      { family: 'Oswald' },         // Condensed, tense
      { family: 'Black Ops One' },  // Military, gritty
      { family: 'Russo One' },      // Geometric bold, authoritative
    ],
  },
  {
    id: 'fantasy',
    label: 'Fantasy & Gothic',
    fonts: [
      { family: 'Cinzel Decorative' }, // Epic fantasy, ornate
      { family: 'Metamorphous' },      // Medieval illuminated manuscript feel
      { family: 'Eagle Lake' },        // Fantasy adventure, slightly runic
      { family: 'UnifrakturMaguntia' },// Blackletter Gothic — horror/dark fantasy
      { family: 'Pirata One' },        // Dark fantasy, pirate, occult
      { family: 'Uncial Antiqua' },    // Celtic/ancient manuscript
    ],
  },
  {
    id: 'romance',
    label: 'Romance & Script',
    fonts: [
      { family: 'Great Vibes' },    // Elegant flowing calligraphy
      { family: 'Kaushan Script' }, // Bold expressive script — more readable on covers
      { family: 'Italianno' },      // Ultra-thin elegant cursive
      { family: 'Sacramento' },     // Thin pointed pen calligraphy
      { family: 'Pinyon Script' },  // Classic penmanship, refined
    ],
  },
  {
    id: 'scifi',
    label: 'Sci-Fi & Future',
    fonts: [
      { family: 'Orbitron' },       // Space-age geometric — sci-fi staple
      { family: 'Audiowide' },      // Rounded tech, feels digital
      { family: 'Exo 2' },          // Futuristic humanist, very versatile
    ],
  },
  {
    id: 'handcrafted',
    label: 'Handcrafted',
    fonts: [
      { family: 'Permanent Marker' },// Bold marker — raw, editorial feel
      { family: 'Amatic SC' },       // Thin hand-drawn caps — quirky literary
      { family: 'Caveat' },          // Casual handwriting, personal memoirs
      { family: 'Special Elite' },   // Vintage typewriter — mystery, noir
      { family: 'Rye' },             // Western wood type — worn, vintage
      { family: 'Pacifico' },        // Bold retro script — playful, children's
    ],
  },
];

// Flat list for backward compat — derived from categories
export const FONT_FAMILIES: string[] = COVER_FONT_CATEGORIES.flatMap(c => c.fonts.map(f => f.family));

// ─── Dimension helper ────────────────────────────────────────────────────────

export interface CoverDimensions {
  pageW: number;
  pageH: number;
  coverW: number;
  coverH: number;
  spineW: number;
  scale: number;
  guides: CanvasGuides;
}

/**
 * Compute the exact full-cover page dimensions (in inches) matching the
 * PDF template generated by generateCoverTemplate.ts, then derive pixel
 * positions at a scale that fits CANVAS_DISPLAY_HEIGHT.
 */
export function computeCoverDimensions(
  bookSize: string,
  paperType: string,
  bindingType: string,
  pageCount: number,
): CoverDimensions {
  const dims =
    BOOK_COVER_DIMENSIONS[bookSize as keyof typeof BOOK_COVER_DIMENSIONS] ||
    BOOK_COVER_DIMENSIONS[BOOK_SIZE['6x9']];
  const coverW = dims.width;
  const coverH = dims.height;

  const paper = PAPER_PROPERTIES[paperType as PaperType] || PAPER_PROPERTIES['Cream Paper' as PaperType];
  const offset = BINDING_SPINE_OFFSET[bindingType as BindingType] ?? 0.6;
  const spineWidthMm = ((pageCount / 2) * paper.gsm * paper.bulk) / 1000 + offset;
  const spineW = Math.round((spineWidthMm / 25.4) * 100) / 100;

  const totalW = coverW + spineW + coverW;
  const pageW = totalW + 2 * BLEED_MARGIN;
  const pageH = coverH + 2 * BLEED_MARGIN;

  // Scale so the full page fits in CANVAS_DISPLAY_HEIGHT pixels
  const scale = CANVAS_DISPLAY_HEIGHT / pageH;

  const bleedPx = BLEED_MARGIN * scale;
  const backWidthPx = coverW * scale;
  const spineWidthPx = spineW * scale;

  return {
    pageW,
    pageH,
    coverW,
    coverH,
    spineW,
    scale,
    guides: {
      bleedPx,
      backWidthPx,
      spineWidthPx,
      frontStartPx: bleedPx + backWidthPx + spineWidthPx,
    },
  };
}
